// src/pages/Transactions.jsx
import { Fragment, useEffect, useMemo, useState } from "react";
import Select from "react-select";

import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";
import { TeamRoute } from "../components/TeamTag";

// Priority: Daan Mogot first, then Kantor, then anything else
const sortGudangPriority = (a, b) => {
  const nameA = (a.location_name || a.name || "").toLowerCase();
  const nameB = (b.location_name || b.name || "").toLowerCase();

  const aIsDaan = nameA.includes("daan mogot");
  const bIsDaan = nameB.includes("daan mogot");
  if (aIsDaan && !bIsDaan) return -1;
  if (!aIsDaan && bIsDaan) return 1;

  const aIsKantor = nameA.includes("kantor");
  const bIsKantor = nameB.includes("kantor");
  if (aIsKantor && !bIsKantor) return -1;
  if (!aIsKantor && bIsKantor) return 1;

  return 0; // keep original order for the rest
};

// Format a Date as YYYY-MM-DD (local time) for <input type="date">
const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Date N days before today (local time)
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

// "Diperbaiki oleh: X" under the note of a REPAIR row — the person who actually
// fixed the barang (an account, or a free-text name for a vendor), which is not
// necessarily the person who logged it.
function RepairedBy({ tx }) {
  if (tx.transaction_type !== "REPAIR") return null;
  const who = tx.assign_username || tx.assign_name;
  if (!who) return null;
  return <div className="text-xs">Diperbaiki oleh: {who}</div>;
}

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [items, setItems] = useState([]);
  const [allLocations, setAllLocations] = useState([]);

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState("");
  // Default filter: show ALL transaction types (otherwise OUT/RETURN rows get
  // hidden right after creating them, which looks like "no history").
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  // Default range: 3 days ago -> today
  const [dateFrom, setDateFrom] = useState(() => toYMD(daysAgo(3)));
  const [dateTo, setDateTo] = useState(() => toYMD(new Date()));

  // form state
  const [selectedItem, setSelectedItem] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState(null); // FROM
  const [selectedToLocationId, setSelectedToLocationId] = useState(null); // TO (TRANSFER only)
  // Defaults to OUT (Keluar) rather than IN (Stock) — hasil meet.txt bug#3:
  // before an item is picked, typeOptions below still shows every type
  // (correctly, since the location-owning kelompok isn't known yet), but
  // starting on "Stock" reads as if stock-in were the normal case when most
  // requests through this form are outgoing. Unconditional across roles: a
  // role-conditional default would have to wait on the async /api/auth/me/
  // fetch first, for a purely cosmetic default value.
  const [transactionType, setTransactionType] = useState("OUT");
  const [quantity, setQuantity] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [assignName, setAssignName] = useState(""); // OUT only: who the item goes to
  const [toPosisi, setToPosisi] = useState(""); // TRANSFER only: posisi at destination gudang
  // items queued for this same transaction (multi-item batch), each { item_id, quantity, label }
  const [lineItems, setLineItems] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  // Per-field validation errors (hasil meet.txt: "field yang membuat eror
  // entry kasih warna merah di kotaknya dan kasih alasan juga") — keyed by
  // field name, e.g. { location: "...", quantity: "..." }. formError above
  // stays for things that don't belong to one specific input (a rejected
  // submit from the backend, network failures).
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Plain <input>/<textarea> border color, swapped rather than appended —
  // Tailwind utility classes have no reliable override order, so the neutral
  // and error border colors are mutually exclusive, never both present.
  const fieldInputClass = (field) =>
    `w-full rounded-md border px-3 py-2 text-sm bg-white/80 dark:bg-white/5 focus:outline-none focus:ring-2 ${
      fieldErrors[field]
        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border-white/50 dark:border-white/15 focus:ring-brand-500 focus:border-brand-500"
    }`;

  function FieldError({ field }) {
    if (!fieldErrors[field]) return null;
    return <p className="mt-1 text-[11px] text-red-600">{fieldErrors[field]}</p>;
  }

  // current user role + pending-approval queue (admins only)
  const [role, setRole] = useState(null);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [actingId, setActingId] = useState(null);

  const isAdmin = role === "admin" || role === "superadmin";
  const isUser = role === "user";

  // who am I?
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/auth/me/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setRole(data?.role ?? null))
      .catch(() => setRole(null));
  }, []);

  // load the pending-approval queue (admins/superadmins only)
  const loadPending = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    setPendingLoading(true);
    fetch(`${API_BASE_URL}/api/transactions/pending/?page=1&page_size=100`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        const rows = Array.isArray(data) ? data : data.results || [];
        setPending(rows);
      })
      .catch(() => {})
      .finally(() => setPendingLoading(false));
  };

  useEffect(() => {
    if (isAdmin) loadPending();
  }, [isAdmin]);

  // fetch items (one-time)
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const baseHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    fetch(`${API_BASE_URL}/api/items/?page=1&page_size=10000`, {
      headers: { "Content-Type": "application/json", ...baseHeaders },
    })
      .then((res) => res.json())
      .then((itemData) => {
        const itemResults = Array.isArray(itemData) ? itemData : itemData.results || [];
        setItems(itemResults);
      })
      .catch((err) => {
        console.log("Error fetching items:", err);
      });
  }, []);

  // fetch transactions (paged)
  useEffect(() => {
    setLoading(true);

    const token = localStorage.getItem("accessToken");
    const baseHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const params = new URLSearchParams();
    params.set("page", page);
    params.set("page_size", pageSize);

    if (search.trim()) params.set("search", search.trim());
    if (typeFilter !== "ALL") params.set("transaction_type", typeFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);

    fetch(`${API_BASE_URL}/api/transactions/?${params.toString()}`, {
      headers: { "Content-Type": "application/json", ...baseHeaders },
    })
      .then((res) => res.json())
      .then((txData) => {
        const txResults = Array.isArray(txData) ? txData : txData.results || [];
        setTransactions(txResults);

        if (!Array.isArray(txData) && typeof txData?.count === "number") {
          setTotalCount(txData.count);
        } else {
          setTotalCount(txResults.length);
        }

        setLoading(false);
      })
      .catch((err) => {
        console.log("Error fetching transactions:", err);
        setLoading(false);
      });
  }, [page, search, typeFilter, dateFrom, dateTo]);

  // load locations
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    fetch(`${API_BASE_URL}/api/locations/`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const locs = Array.isArray(data) ? data : data.results || [];
        setAllLocations(locs);
      })
      .catch((err) => console.error("Error loading locations:", err));
  }, []);

  // selected item
  const selectedItemObj = useMemo(
    () => items.find((it) => it.id === Number(selectedItem)),
    [items, selectedItem]
  );

  // location stocks for selected item
  const locationStocks = useMemo(
    () => selectedItemObj?.location_stocks || [],
    [selectedItemObj]
  );

  // sorted lists (for dropdown ordering)
  const sortedLocationStocks = useMemo(
    () => [...locationStocks].sort(sortGudangPriority),
    [locationStocks]
  );

  const sortedAllLocations = useMemo(
    () => [...allLocations].sort(sortGudangPriority),
    [allLocations]
  );

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, dateFrom, dateTo]);

  // when transaction type changes
  useEffect(() => {
    setFormError("");
    setFormSuccess("");
    if (transactionType !== "TRANSFER") {
      setSelectedToLocationId(null);
      setToPosisi("");
    }
    if (transactionType !== "OUT") {
      setAssignName("");
    }
  }, [transactionType]);

  // auto-pick location when item changes. Deliberately does NOT reset the
  // location back to null when the item selection is cleared (e.g. right
  // after "+ Item" queues a line and clears the item field) — each queued
  // line already snapshotted its own location, so clearing the picker here
  // must not disturb the location the user is about to pick for the next one.
  useEffect(() => {
    if (!selectedItemObj) return;

    if (!locationStocks.length) {
      setSelectedLocationId(null);
      return;
    }

    // keep the current selection if it's still valid for this item
    if (locationStocks.some((ls) => ls.location_id === selectedLocationId)) {
      return;
    }

    if (locationStocks.length === 1) {
      setSelectedLocationId(locationStocks[0].location_id);
      return;
    }

    const daan = locationStocks.find((ls) =>
      (ls.location_name || "").toLowerCase().includes("daan mogot")
    );
    setSelectedLocationId((daan || locationStocks[0]).location_id);
  }, [selectedItemObj, locationStocks]);

  // Does the caller's kelompok keep the currently selected gudang? The backend
  // computes this per gudang (LocationSerializer.can_manage) so the rule lives
  // in one place; absent (older payload / scoping off) means unrestricted.
  const canManageSelectedLocation = useMemo(() => {
    if (!selectedLocationId) return true;
    const loc = allLocations.find((l) => l.id === selectedLocationId);
    return loc?.can_manage !== false;
  }, [allLocations, selectedLocationId]);

  // A kelompok that doesn't keep this gudang is its customer: it can ask for
  // barang out, nothing else. Mirrors Transaction.NON_OWNER_TYPES.
  const typeOptions = useMemo(() => {
    const all = [
      { value: "IN",       label: "Stock"        },
      { value: "OUT",      label: "Keluar"       },
      { value: "RETURN",   label: "Kembali"      },
      { value: "DAMAGED",  label: "Rusak"        },
      { value: "TRANSFER", label: "Mutasi Gudang"},
    ];
    if (canManageSelectedLocation) return all;
    return all.filter((o) => o.value === "OUT");
  }, [canManageSelectedLocation]);

  // Snap back to the only type still on offer if the gudang just changed to
  // one this kelompok doesn't keep — otherwise the form would keep a type the
  // dropdown no longer shows and only fail at submit.
  useEffect(() => {
    if (!canManageSelectedLocation && transactionType !== "OUT") {
      setTransactionType("OUT");
    }
  }, [canManageSelectedLocation, transactionType]);

  // selected location stock (used for preview)
  const selectedLocationStock = useMemo(() => {
    if (!selectedLocationId) return null;

    // OUT/RETURN/DAMAGED/TRANSFER: must exist in locationStocks
    const existing = locationStocks.find(
      (ls) => ls.location_id === selectedLocationId
    );
    if (existing) return existing;

    // IN: can be new location
    const loc = allLocations.find((l) => l.id === selectedLocationId);
    return {
      location_id: selectedLocationId,
      location_name: loc?.name || "-",
      available: 0,
      borrowed: 0,
      damaged: 0,
    };
  }, [locationStocks, selectedLocationId, allLocations]);

  // badge colours
  const getTypeClasses = (type) => {
    switch (type) {
      case "IN":
        return "badge badge-success";
      case "OUT":
        return "badge badge-warning";
      case "DAMAGED":
        return "badge badge-danger";
      case "REPAIR":
        return "badge badge-success";
      case "RETURN":
        return "bg-black/5 dark:bg-white/10 text-strong border border-white/40 dark:border-white/10";
      case "TRANSFER":
        return "badge badge-info";
      case "TEMP_OUT":
        return "badge badge-danger";
      default:
        return "bg-black/5 dark:bg-white/10 text-strong border border-white/40 dark:border-white/10";
    }
  };

  // label mapping
  const getTypeLabel = (type) => {
    switch (type) {
      case "IN":
        return "Stock";
      case "OUT":
        return "Keluar";
      case "DAMAGED":
        return "Rusak";
      case "REPAIR":
        return "Diperbaiki";
      case "RETURN":
        return "Kembali";
      case "TRANSFER":
        return "Mutasi Gudang";
      // Not "temporary" — the DB value is a historical misnomer. This is the
      // write-off: barang that went out and is never coming back.
      case "TEMP_OUT":
        return "Terpakai";
      default:
        return type;
    }
  };

  // status badge styling
  const getStatusClasses = (status) => {
    switch (status) {
      case "PENDING":
        return "badge badge-warning";
      case "APPROVED":
        return "badge badge-success";
      case "REJECTED":
        return "badge badge-danger";
      default:
        return "bg-black/5 dark:bg-white/10 text-strong border border-white/40 dark:border-white/10";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "Menunggu Acc";
      case "APPROVED":
        return "Disetujui";
      case "REJECTED":
        return "Ditolak";
      default:
        return status || "-";
    }
  };

  // refresh the visible history + item stocks after an approval decision
  const refreshHistoryAndItems = () => {
    const token = localStorage.getItem("accessToken");
    const baseHeaders = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const params = new URLSearchParams();
    params.set("page", page);
    params.set("page_size", pageSize);
    if (search.trim()) params.set("search", search.trim());
    if (typeFilter !== "ALL") params.set("transaction_type", typeFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);

    fetch(`${API_BASE_URL}/api/transactions/?${params.toString()}`, { headers: baseHeaders })
      .then((res) => res.json())
      .then((txData) => {
        const txResults = Array.isArray(txData) ? txData : txData.results || [];
        setTransactions(txResults);
        if (!Array.isArray(txData) && typeof txData?.count === "number") {
          setTotalCount(txData.count);
        }
      })
      .catch(() => {});

    fetch(`${API_BASE_URL}/api/items/?page=1&page_size=10000`, { headers: baseHeaders })
      .then((res) => res.json())
      .then((itemData) => {
        const itemResults = Array.isArray(itemData) ? itemData : itemData.results || [];
        setItems(itemResults);
      })
      .catch(() => {});
  };

  // approve ("acc") or reject a pending transaction (admins only).
  // `outcome` is only sent for OUT: the gudang owner deciding, at handover
  // time, whether the barang is coming back. Omitted for every other type,
  // where the backend keeps its original behaviour.
  const handleDecision = (txId, decision, outcome = null) => {
    const token = localStorage.getItem("accessToken");
    setActingId(txId);
    fetch(`${API_BASE_URL}/api/transactions/${txId}/${decision}/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(outcome ? { body: JSON.stringify(outcome) } : {}),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          let msg = "Failed to process transaction.";
          if (data && typeof data === "object") {
            const firstKey = Object.keys(data)[0];
            if (firstKey && Array.isArray(data[firstKey])) msg = data[firstKey][0];
            else if (firstKey && typeof data[firstKey] === "string") msg = data[firstKey];
            else if (data.detail) msg = data.detail;
          }
          throw new Error(msg);
        }
        return res.json();
      })
      .then(() => {
        // drop it from the pending list and refresh history + stocks
        setPending((prev) => prev.filter((t) => t.id !== txId));
        if (decision === "approve") refreshHistoryAndItems();
      })
      .catch((err) => {
        alert(err.message || "Failed to process transaction.");
      })
      .finally(() => setActingId(null));
  };

  // Inline panel for the two OUT-specific ACC decisions — replaces
  // window.prompt (due date) / window.confirm (pakai habis). hasil meet.txt:
  // the date popup wouldn't let the ACC button be tapped on mobile at all;
  // an inline row expansion has no such platform quirk, and lets "pakai
  // habis" carry a required note the way a bare confirm() never could.
  // { txId, mode: "pinjam" | "habis" } | null
  const [accPanel, setAccPanel] = useState(null);
  const [accDueDate, setAccDueDate] = useState("");
  const [accNote, setAccNote] = useState("");
  const [accPanelError, setAccPanelError] = useState("");

  const openAccPanel = (tx, mode) => {
    if (mode === "pinjam") {
      const suggested = new Date();
      suggested.setDate(suggested.getDate() + 7);
      setAccDueDate(suggested.toISOString().slice(0, 10));
    }
    setAccNote("");
    setAccPanelError("");
    setAccPanel({ txId: tx.id, mode });
  };

  const closeAccPanel = () => {
    setAccPanel(null);
    setAccDueDate("");
    setAccNote("");
    setAccPanelError("");
  };

  const confirmAccPanel = () => {
    if (!accPanel) return;
    if (accPanel.mode === "pinjam") {
      if (!accDueDate) {
        setAccPanelError("Tanggal kembali wajib diisi.");
        return;
      }
      handleDecision(accPanel.txId, "approve", { expects_return: true, due_date: accDueDate });
    } else {
      // Admin inventory: barang terpakai permanen wajib kasih note, untuk
      // memudahkan tracking barang.
      if (!accNote.trim()) {
        setAccPanelError("Catatan wajib diisi untuk barang yang dipakai habis.");
        return;
      }
      handleDecision(accPanel.txId, "approve", { expects_return: false, approval_note: accNote.trim() });
    }
    closeAccPanel();
  };

  // options for item dropdown — shows what's AVAILABLE across every gudang
  // (quantity_available), not stock_awal (a separate, historical "initial
  // stock entered" figure). hasil meet.txt: showing stock_awal here read as
  // if it were current availability, which it isn't — only once a gudang is
  // picked below does the number narrow to that specific gudang's available.
  const itemOptions = items.map((item) => ({
    value: item.id,
    label: `${item.code} — ${item.name} (Available: ${item.quantity_available ?? 0}, Damaged: ${item.quantity_damaged ?? 0})`,
  }));

  // Validates the fields currently sitting in the entry form, independent of
  // whether they're about to be queued as a line or folded in at submit time.
  // Returns an object keyed by field name — {} means clean — so each
  // offending input can carry its own red border + reason instead of one
  // generic message at the bottom of the form.
  const validateCurrentEntry = (qtyNumber) => {
    const errors = {};
    if (!qtyNumber || qtyNumber <= 0) {
      errors.quantity = "Quantity harus lebih dari 0.";
    }
    if (!selectedLocationId) {
      errors.location = "Pilih gudang terlebih dahulu.";
    }
    if (transactionType === "TRANSFER") {
      if (!selectedToLocationId) {
        errors.toLocation = "Gudang tujuan (To) wajib diisi untuk mutasi.";
      } else if (selectedLocationId && Number(selectedLocationId) === Number(selectedToLocationId)) {
        errors.toLocation = "Gudang asal dan tujuan harus berbeda.";
      }
    }
    if (transactionType === "DAMAGED" && !adminNote.trim()) {
      errors.adminNote = "Catatan wajib diisi untuk transaksi Rusak (Damaged).";
    }
    if (transactionType === "OUT" && !assignName.trim()) {
      errors.assignName = "Assign / Atas Nama wajib diisi untuk barang keluar.";
    }
    return errors;
  };

  // Snapshots the current entry form into a self-contained "package": its own
  // item, quantity, type, location and note — not shared with other lines.
  const buildLineFromCurrentEntry = (itemId, qtyNumber) => {
    const opt = itemOptions.find((o) => o.value === itemId);
    const locName =
      sortedAllLocations.find((l) => l.id === selectedLocationId)?.name ||
      selectedLocationStock?.location_name ||
      "-";
    const toLocName =
      transactionType === "TRANSFER"
        ? allLocations.find((l) => l.id === selectedToLocationId)?.name || "-"
        : null;

    return {
      item_id: itemId,
      quantity: qtyNumber,
      label: opt?.label || `#${itemId}`,
      transaction_type: transactionType,
      location_id: selectedLocationId,
      location_name: locName,
      to_location_id: transactionType === "TRANSFER" ? selectedToLocationId : null,
      to_location_name: toLocName,
      to_posisi: transactionType === "TRANSFER" ? toPosisi.trim() : "",
      assign_name: transactionType === "OUT" ? assignName.trim() : "",
      admin_note: adminNote.trim(),
    };
  };

  // queue the currently-selected item as one more self-contained "package"
  // (own item, qty, type, location, note) in this same submission
  const handleAddLineItem = () => {
    setFormError("");
    const qtyNumber = Number(quantity);
    const errors = validateCurrentEntry(qtyNumber);
    if (!selectedItem) {
      errors.item = "Pilih item terlebih dahulu sebelum menambahkan ke daftar.";
    } else if (lineItems.some((li) => li.item_id === Number(selectedItem))) {
      errors.item = "Item sudah ada di daftar. Hapus dulu jika ingin mengubah qty.";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const itemId = Number(selectedItem);
    setLineItems((prev) => [...prev, buildLineFromCurrentEntry(itemId, qtyNumber)]);

    // clear only the item-specific fields; type/location stay so consecutive
    // packages with the same type/location don't need reselecting every time
    setSelectedItem("");
    setQuantity("");
    setAdminNote("");
    setAssignName("");
  };

  const handleRemoveLineItem = (itemId) => {
    setLineItems((prev) => prev.filter((li) => li.item_id !== itemId));
  };

  // filters
  const filteredTransactions = Array.isArray(transactions) ? transactions : [];

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    const d = new Date(isoString);
    return d.toLocaleString("en-SG", { dateStyle: "short", timeStyle: "short" });
  };

  // preview for non-transfer types
  const computePreview = () => {
    if (transactionType === "TRANSFER") return null;

    const qtyNumber = Number(quantity);
    if (!selectedItemObj || !selectedLocationStock || !qtyNumber || qtyNumber <= 0) {
      return null;
    }

    const currentAvail = Number(selectedLocationStock.available ?? 0);
    const currentBorrowed = Number(selectedLocationStock.borrowed ?? 0);
    const currentDamaged = Number(selectedLocationStock.damaged ?? 0);

    let deltaAvail = 0;
    let deltaBorrowed = 0;
    let deltaDamaged = 0;

    switch (transactionType) {
      case "IN":
        deltaAvail = qtyNumber;
        break;
      case "OUT":
        deltaAvail = -qtyNumber;
        deltaBorrowed = qtyNumber;
        break;
      case "RETURN":
        deltaAvail = qtyNumber;
        deltaBorrowed = -qtyNumber;
        break;
      case "DAMAGED":
        deltaAvail = -qtyNumber;
        deltaDamaged = qtyNumber;
        break;
      default:
        break;
    }

    return {
      currentAvail,
      currentBorrowed,
      currentDamaged,
      newAvail: currentAvail + deltaAvail,
      newBorrowed: currentBorrowed + deltaBorrowed,
      newDamaged: currentDamaged + deltaDamaged,
    };
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // builds the final "note" text for one self-contained package/line
  const buildLineNote = (li) => {
    let note =
      li.transaction_type === "TRANSFER"
        ? `TRANSFER | ${li.label} | ${li.location_name} -> ${li.to_location_name || "-"}`
        : `${li.transaction_type} | ${li.label} | ${li.location_name}`;

    if (li.transaction_type === "OUT" && (li.assign_name || "").trim()) {
      note += ` | Atas Nama: ${li.assign_name.trim()}`;
    }
    if ((li.admin_note || "").trim()) {
      note += ` | ${li.admin_note.trim()}`;
    }
    return note;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");
    setFieldErrors({});

    // fold whatever is currently sitting in the entry fields into the list
    // of packages, alongside anything already queued via "+ Item" — each
    // package (queued or in-progress) carries its own type/location/note.
    let allItems = [...lineItems];
    if (selectedItem) {
      const qtyNumber = Number(quantity);
      const errors = validateCurrentEntry(qtyNumber);
      const itemId = Number(selectedItem);
      if (allItems.some((li) => li.item_id === itemId)) {
        errors.item = "Item sudah ada di daftar. Hapus salah satu sebelum submit.";
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      allItems.push(buildLineFromCurrentEntry(itemId, qtyNumber));
    }

    if (allItems.length === 0) {
      setFieldErrors({ item: "Please select an item." });
      return;
    }

    // per-item stock preview check (frontend check only, single-package
    // submissions; for multi-package submissions the backend is the source
    // of truth per row regardless)
    if (allItems.length === 1 && allItems[0].transaction_type !== "TRANSFER") {
      const preview = computePreview();
      if (preview && selectedLocationStock) {
        if ((transactionType === "OUT" || transactionType === "DAMAGED") && preview.newAvail < 0) {
          setFieldErrors({ quantity: "Not enough available stock at this location for this transaction." });
          return;
        }

        if (transactionType === "RETURN" && preview.newBorrowed < 0) {
          setFieldErrors({ quantity: "Return quantity is more than currently borrowed at this location." });
          return;
        }
      }
    }

    const payload = {
      items: allItems.map((li) => {
        const row = {
          item_id: li.item_id,
          quantity: li.quantity,
          transaction_type: li.transaction_type,
          location_id: li.location_id,
          note: buildLineNote(li),
        };
        if (li.transaction_type === "TRANSFER") {
          row.to_location_id = li.to_location_id;
          if ((li.to_posisi || "").trim()) row.to_posisi = li.to_posisi.trim();
        }
        // Also sent as a real column now, not only baked into the note text —
        // that's what makes "apa saja yang dipegang X" answerable at all.
        // buildLineNote still writes it into the note so the history line
        // reads the same as every row before this change.
        if (li.transaction_type === "OUT" && (li.assign_name || "").trim()) {
          row.assign_name = li.assign_name.trim();
        }
        return row;
      }),
    };

    setSubmitting(true);

    const token = localStorage.getItem("accessToken");
    fetch(`${API_BASE_URL}/api/transactions/create/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          let msg = "Failed to create transaction.";
          if (data && typeof data === "object") {
            const firstKey = Object.keys(data)[0];
            if (firstKey && Array.isArray(data[firstKey])) msg = data[firstKey][0];
            else if (data.detail) msg = data.detail;
          }
          throw new Error(msg);
        }
        return res.json();
      })
      .then((createdTx) => {
        setFormSuccess(
          isUser
            ? "Transaksi terkirim. Menunggu persetujuan (acc) admin."
            : "Transaction created successfully."
        );
        setPage(1);

        // refetch transactions page 1
        const params = new URLSearchParams();
        params.set("page", 1);
        params.set("page_size", pageSize);

        if (search.trim()) params.set("search", search.trim());
        if (typeFilter !== "ALL") params.set("transaction_type", typeFilter);
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);

        fetch(`${API_BASE_URL}/api/transactions/?${params.toString()}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        })
          .then((res) => res.json())
          .then((txData) => {
            const txResults = Array.isArray(txData) ? txData : txData.results || [];
            setTransactions(txResults);
            if (!Array.isArray(txData) && typeof txData?.count === "number") {
              setTotalCount(txData.count);
            }
          })
          .catch(() => { });


        // Refresh items so per-location stocks update immediately (especially TRANSFER)
        fetch(`${API_BASE_URL}/api/items/?page=1&page_size=10000`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        })
          .then((res) => res.json())
          .then((itemData) => {
            const itemResults = Array.isArray(itemData) ? itemData : itemData.results || [];
            setItems(itemResults);
          })
          .catch(() => { });

        // reset form
        setSelectedItem("");
        setSelectedLocationId(null);
        setSelectedToLocationId(null);
        setTransactionType("IN");
        setQuantity("");
        setAdminNote("");
        setAssignName("");
        setToPosisi("");
        setLineItems([]);
      })
      .catch((err) => {
        console.error(err);
        setFormError(err.message || "Failed to create transaction.");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Transactions</h1>

      {/* New transaction form */}
      <div className="glass-card p-4 mb-5">
        <h2 className="text-sm font-semibold mb-3">New Transaction</h2>

        <form onSubmit={handleSubmit} className="grid gap-3 md:grid-cols-4 md:items-end">
          {/* Item */}
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-muted mb-1">Item</label>

            <Select
              classNamePrefix="react-select"
              placeholder="Search or select item."
              isClearable
              isSearchable
              options={itemOptions}
              value={itemOptions.find((opt) => opt.value === Number(selectedItem)) || null}
              onChange={(option) => {
                setSelectedItem(option ? option.value : "");
                setFormError("");
                setFormSuccess("");
                clearFieldError("item");
              }}
              filterOption={(option, rawInput) =>
                option.label.toLowerCase().includes(rawInput.toLowerCase())
              }
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: fieldErrors.item ? "var(--danger)" : base.borderColor,
                }),
              }}
            />
            <FieldError field="item" />

            {/* Location area */}
            <div className="mt-2 text-xs">
              <div className="font-medium text-muted mb-2">Location</div>

              {transactionType === "TRANSFER" ? (
                <>
                  {/* FROM */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-muted mb-1">
                      From Gudang
                    </label>

                    <CustomSelect
                      value={selectedLocationId ?? ""}
                      onChange={(v) => {
                        const val = v ? Number(v) : null;
                        setSelectedLocationId(val);
                        clearFieldError("location");
                        // Live-check against the already-picked "To" rather than
                        // waiting for the next submit/+ Item attempt — this used
                        // to be a separate always-mounted message; folding it
                        // into the same field-error slot keeps one mechanism.
                        if (val && selectedToLocationId && val === Number(selectedToLocationId)) {
                          setFieldErrors((prev) => ({ ...prev, toLocation: "Gudang asal dan tujuan harus berbeda." }));
                        } else {
                          clearFieldError("toLocation");
                        }
                      }}
                      placeholder="Select From Gudang"
                      error={!!fieldErrors.location}
                      options={sortedLocationStocks.map((ls) => ({
                        value: String(ls.location_id),
                        label: `${ls.location_name || "-"} (Available: ${ls.available ?? 0})`,
                      }))}
                    />
                    <FieldError field="location" />
                  </div>

                  {/* TO */}
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">
                      To Gudang
                    </label>

                    <CustomSelect
                      value={selectedToLocationId ?? ""}
                      onChange={(v) => {
                        const val = v ? Number(v) : null;
                        setSelectedToLocationId(val);
                        if (val && selectedLocationId && val === Number(selectedLocationId)) {
                          setFieldErrors((prev) => ({ ...prev, toLocation: "Gudang asal dan tujuan harus berbeda." }));
                        } else {
                          clearFieldError("toLocation");
                        }
                      }}
                      placeholder="Select To Gudang"
                      error={!!fieldErrors.toLocation}
                      options={sortedAllLocations
                        .filter((loc) => Number(selectedLocationId) !== Number(loc.id))
                        .map((loc) => ({ value: String(loc.id), label: loc.name }))}
                    />
                    <FieldError field="toLocation" />
                  </div>

                  {/* Posisi at destination gudang */}
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-muted mb-1">
                      Detail Lokasi / Posisi di Gudang Tujuan (opsional)
                    </label>
                    <input
                      type="text"
                      value={toPosisi}
                      onChange={(e) => setToPosisi(e.target.value)}
                      placeholder="mis. Rak C3"
                      className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm bg-white/80 dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* NON-TRANSFER: one location dropdown */}
                  <CustomSelect
                    value={selectedLocationId ?? ""}
                    onChange={(v) => { setSelectedLocationId(v ? Number(v) : null); clearFieldError("location"); }}
                    placeholder="Select Gudang"
                    error={!!fieldErrors.location}
                    options={
                      transactionType === "IN"
                        ? sortedAllLocations.map((loc) => {
                            const ls = locationStocks.find((s) => s.location_id === loc.id);
                            return { value: String(loc.id), label: `${loc.name} (Available: ${ls?.available ?? 0})` };
                          })
                        : sortedLocationStocks.map((ls) => ({
                            value: String(ls.location_id),
                            label: `${ls.location_name || "-"} (Available: ${ls.available ?? 0})`,
                          }))
                    }
                  />
                  <FieldError field="location" />
                </>
              )}
            </div>
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Transaction type</label>
            <CustomSelect
              value={transactionType}
              onChange={setTransactionType}
              options={typeOptions}
            />
            {!canManageSelectedLocation && (
              <p className="text-[11px] mt-1.5 text-muted">
                Gudang ini diurus kelompok lain — kamu bisa mengajukan permintaan
                barang keluar. Pemilik gudang yang menentukan dipinjam atau dipakai
                habis waktu meng-ACC.
              </p>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Quantity</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => { setQuantity(e.target.value); clearFieldError("quantity"); }}
                className={fieldInputClass("quantity")}
                placeholder="e.g. 2"
              />
              <button
                type="button"
                onClick={handleAddLineItem}
                title="Simpan sebagai bungkusan sendiri (type/location/note masing-masing), lalu tambah item lain"
                className="shrink-0 inline-flex items-center justify-center rounded-md border border-brand-500/50 text-brand-600 text-sm font-semibold px-3 py-2 hover:bg-brand-500/10"
              >
                + Item
              </button>
            </div>
            <FieldError field="quantity" />
          </div>

          {/* Assign (OUT only) */}
          {transactionType === "OUT" && (
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-muted mb-1">
                Assign / Atas Nama *
              </label>
              <input
                type="text"
                value={assignName}
                onChange={(e) => { setAssignName(e.target.value); clearFieldError("assignName"); }}
                className={fieldInputClass("assignName")}
                placeholder="Nama orang yang menerima barang"
              />
              <FieldError field="assignName" />
            </div>
          )}

          {/* Queued packages for this same submission — each one carries its
              own type/location/note, they are NOT shared across the list */}
          {lineItems.length > 0 && (
            <div className="md:col-span-4">
              <label className="block text-xs font-medium text-muted mb-1">
                Item dalam transaksi ini ({lineItems.length})
              </label>
              <div className="flex flex-wrap gap-2">
                {lineItems.map((li) => (
                  <span
                    key={li.item_id}
                    className="inline-flex items-center gap-2 rounded-full border border-white/40 dark:border-white/15 bg-white/60 dark:bg-white/5 px-3 py-1 text-xs"
                  >
                    {li.label} × {li.quantity} — {getTypeLabel(li.transaction_type)} @ {li.location_name}
                    {li.transaction_type === "TRANSFER" && ` → ${li.to_location_name}`}
                    <button
                      type="button"
                      onClick={() => handleRemoveLineItem(li.item_id)}
                      className="text-red-500 hover:text-red-700 font-bold leading-none"
                      title="Hapus item ini"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Note */}
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-muted mb-1">
              Note {transactionType === "DAMAGED" ? "*" : "(optional)"}
            </label>

            <textarea
              rows={2}
              value={adminNote}
              onChange={(e) => { setAdminNote(e.target.value); clearFieldError("adminNote"); }}
              placeholder="Add admin note..."
              className={fieldInputClass("adminNote")}
            />
            <FieldError field="adminNote" />
          </div>

          {/* Submit */}
          <div className="md:col-span-1 flex items-end">
            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center rounded-md bg-brand-600 text-white text-sm font-semibold px-4 py-2 hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Saving..." : "Save Transaction"}
            </button>
          </div>
        </form>

        {/* stock preview (non-transfer only) */}
        {computePreview() && selectedLocationStock && (
          <div className="mt-3 rounded-md border border-white/40 dark:border-white/10 bg-brand-500/5 px-3 py-2 text-xs text-strong">
            <div className="font-semibold mb-1">
              Stock preview — {selectedLocationStock.location_name || "-"}
            </div>
            {(() => {
              const p = computePreview();
              if (!p) return null;
              return (
                <div className="space-y-1">
                  <div>
                    <span className="font-medium">Available:</span> {p.currentAvail} →{" "}
                    <span className="font-semibold">{p.newAvail}</span>
                  </div>
                  {(transactionType === "OUT" || transactionType === "RETURN") && (
                    <div>
                      <span className="font-medium">Borrowed:</span> {p.currentBorrowed} →{" "}
                      <span className="font-semibold">{p.newBorrowed}</span>
                    </div>
                  )}
                  {transactionType === "DAMAGED" && (
                    <div>
                      <span className="font-medium">Damaged:</span> {p.currentDamaged} →{" "}
                      <span className="font-semibold">{p.newDamaged}</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* role hint for basic users */}
        {isUser && (
          <div className="mt-3 note-warning">
            Transaksi yang kamu buat berstatus <b>Menunggu Acc</b> dan baru
            mengubah stok setelah disetujui admin / superadmin.
          </div>
        )}

        {/* messages */}
        {(formError || formSuccess) && (
          <div className="mt-3 text-xs">
            {formError && <div className="text-red-600">{formError}</div>}
            {formSuccess && <div className="text-green-600">{formSuccess}</div>}
          </div>
        )}
      </div>

      {/* Pending approval queue — admins / superadmins only */}
      {isAdmin && (
        <div className="glass-card p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">
              Menunggu Persetujuan (Acc)
              {pending.length > 0 && (
                <span className="ml-2 badge badge-warning">
                  {pending.length}
                </span>
              )}
            </h2>
            <button
              type="button"
              onClick={loadPending}
              className="text-xs font-medium text-brand-600 hover:text-brand-700"
            >
              Refresh
            </button>
          </div>

          {pendingLoading ? (
            <div className="text-xs text-muted">Loading…</div>
          ) : pending.length === 0 ? (
            <div className="text-xs text-muted">Tidak ada transaksi yang menunggu persetujuan.</div>
          ) : (
            <div className="glass-table-wrap overflow-x-auto">
              <table className="glass-table">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">Date / Time</th>
                    <th className="px-4 py-2 text-left">Item</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-right">Quantity</th>
                    <th className="px-4 py-2 text-left cell-wrap">Note</th>
                    <th className="px-4 py-2 text-left">Diajukan oleh</th>
                    <th className="px-4 py-2 text-left">Kelompok</th>
                    <th className="px-4 py-2 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((tx) => {
                    const panelOpen = accPanel?.txId === tx.id;
                    return (
                    <Fragment key={tx.id}>
                    <tr className="border-t">
                      <td className="px-4 py-2">{formatDateTime(tx.created_at)}</td>
                      <td className="px-4 py-2">
                        <div className="font-semibold">{tx.item?.code || "-"}</div>
                        <div className="text-xs text-muted">{tx.item?.name || ""}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={
                            "inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold " +
                            getTypeClasses(tx.transaction_type)
                          }
                        >
                          {getTypeLabel(tx.transaction_type)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">{tx.quantity}</td>
                      <td className="px-4 py-2 text-muted cell-wrap">
                        {tx.note || "-"}
                        <RepairedBy tx={tx} />
                      </td>
                      <td className="px-4 py-2 text-muted">{tx.user || "-"}</td>
                      <td className="px-4 py-2">
                        <TeamRoute
                          requester={tx.requester_team_name}
                          approver={tx.approver_team_name}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex justify-end gap-2">
                          {/* Barang keluar gets two ACC buttons instead of one:
                              approving it is also deciding whether it comes
                              back, and that decision has no sensible default.
                              Both open an inline panel below this row instead
                              of a popup (hasil meet.txt: window.prompt's date
                              picker couldn't be tapped on mobile). */}
                          {tx.transaction_type === "OUT" ? (
                            <>
                              <button
                                type="button"
                                disabled={actingId === tx.id}
                                onClick={() => openAccPanel(tx, "pinjam")}
                                className="rounded-md bg-green-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-green-700 disabled:opacity-60"
                                title="Barang balik — isi tanggal kembali"
                              >
                                {actingId === tx.id ? "…" : "Acc: Pinjam"}
                              </button>
                              <button
                                type="button"
                                disabled={actingId === tx.id}
                                onClick={() => openAccPanel(tx, "habis")}
                                className="rounded-md bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-700 disabled:opacity-60"
                                title="Barang tidak kembali — langsung keluar dari pembukuan"
                              >
                                Acc: Pakai habis
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              disabled={actingId === tx.id}
                              onClick={() => handleDecision(tx.id, "approve")}
                              className="rounded-md bg-green-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-green-700 disabled:opacity-60"
                            >
                              {actingId === tx.id ? "…" : "Acc"}
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={actingId === tx.id}
                            onClick={() => handleDecision(tx.id, "reject")}
                            className="rounded-md bg-red-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-red-700 disabled:opacity-60"
                          >
                            Tolak
                          </button>
                        </div>
                      </td>
                    </tr>
                    {panelOpen && (
                      <tr className="border-t bg-black/5 dark:bg-white/5">
                        <td colSpan={8} className="px-4 py-3">
                          {accPanel.mode === "pinjam" ? (
                            <div className="flex flex-wrap items-end justify-end gap-3">
                              <div>
                                <label className="block text-xs font-medium text-muted mb-1">
                                  Tanggal kembali *
                                </label>
                                <input
                                  type="date"
                                  value={accDueDate}
                                  onChange={(e) => { setAccDueDate(e.target.value); setAccPanelError(""); }}
                                  className={accPanelError ? "border-red-500 rounded-md px-3 py-1.5 text-sm" : "border-white/50 dark:border-white/15 rounded-md border px-3 py-1.5 text-sm bg-white/80 dark:bg-white/5"}
                                />
                              </div>
                              <button type="button" onClick={confirmAccPanel} disabled={actingId === tx.id}
                                className="rounded-md bg-green-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-green-700 disabled:opacity-60">
                                {actingId === tx.id ? "…" : "Konfirmasi"}
                              </button>
                              <button type="button" onClick={closeAccPanel}
                                className="rounded-md border border-white/50 dark:border-white/15 text-xs font-semibold px-3 py-1.5">
                                Batal
                              </button>
                              {accPanelError && <p className="w-full text-[11px] text-red-600">{accPanelError}</p>}
                            </div>
                          ) : (
                            <div className="flex flex-wrap items-end justify-end gap-3">
                              <div className="flex-1 min-w-[240px]">
                                <label className="block text-xs font-medium text-muted mb-1">
                                  Catatan (wajib) — kenapa barang ini tidak kembali
                                </label>
                                <input
                                  type="text"
                                  value={accNote}
                                  onChange={(e) => { setAccNote(e.target.value); setAccPanelError(""); }}
                                  placeholder="mis. dipakai buat acara X, habis terpakai"
                                  className={`w-full rounded-md px-3 py-1.5 text-sm ${accPanelError ? "border border-red-500" : "border border-white/50 dark:border-white/15 bg-white/80 dark:bg-white/5"}`}
                                />
                              </div>
                              <button type="button" onClick={confirmAccPanel} disabled={actingId === tx.id}
                                className="rounded-md bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-700 disabled:opacity-60">
                                {actingId === tx.id ? "…" : "Konfirmasi"}
                              </button>
                              <button type="button" onClick={closeAccPanel}
                                className="rounded-md border border-white/50 dark:border-white/15 text-xs font-semibold px-3 py-1.5">
                                Batal
                              </button>
                              {accPanelError && <p className="w-full text-[11px] text-red-600">{accPanelError}</p>}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-start sm:gap-4 mb-4">
        <div className="w-full sm:max-w-md">
          <label className="block text-xs font-medium text-muted mb-1">
            Search (item code, name, note)
          </label>
          <input
            type="text"
            placeholder="e.g A-001, microphone, pinjam."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white/80 dark:bg-white/5"
          />
        </div>

        <div className="flex gap-2">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm bg-white/80 dark:bg-white/5"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted mb-1">
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm bg-white/80 dark:bg-white/5"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Transaction type</label>
          <CustomSelect
            value={typeFilter}
            onChange={setTypeFilter}
            className="w-44"
            options={[
              { value: "ALL",      label: "All"          },
              { value: "IN",       label: "Stock"        },
              { value: "OUT",      label: "Keluar"       },
              { value: "RETURN",   label: "Kembali"      },
              { value: "DAMAGED",  label: "Rusak"        },
              { value: "REPAIR",   label: "Diperbaiki"   },
              { value: "TRANSFER", label: "Mutasi Gudang"},
            ]}
          />
        </div>
      </div>

      {/* Table */}
      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">Date / Time</th>
              <th className="px-4 py-2 text-left">Item</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-right">Quantity</th>
              <th className="px-4 py-2 text-left cell-wrap">Note</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Dibuat / Acc</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-center text-muted">
                  Loading transactions.
                </td>
              </tr>
            )}

            {!loading && filteredTransactions.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-center text-muted">
                  No transactions found.
                </td>
              </tr>
            )}

            {!loading &&
              filteredTransactions.map((tx) => (
                <tr key={tx.id} className="border-t">
                  <td className="px-4 py-2">{formatDateTime(tx.created_at)}</td>
                  <td className="px-4 py-2">
                    <div className="font-semibold">{tx.item?.code || "-"}</div>
                    <div className="text-xs text-muted">{tx.item?.name || ""}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        "inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold " +
                        getTypeClasses(tx.transaction_type)
                      }
                    >
                      {getTypeLabel(tx.transaction_type)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">{tx.quantity}</td>
                  <td className="px-4 py-2 text-muted cell-wrap">
                    {tx.note || "-"}
                    <RepairedBy tx={tx} />
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        "inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold " +
                        getStatusClasses(tx.status)
                      }
                    >
                      {getStatusLabel(tx.status)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-muted">
                    <div>{tx.user || "-"}</div>
                    {tx.approved_by && (
                      <div className="text-xs text-muted">acc: {tx.approved_by}</div>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <div className="flex items-center gap-2">
          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            First
          </button>

          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </button>
        </div>

        <div className="text-xs text-muted">
          Page {page} of {totalPages}
        </div>

        <div className="flex items-center gap-2">
          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>

          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page === totalPages}
            onClick={() => setPage(totalPages)}
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
}

export default Transactions;
